import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { teamAPI } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiPlus, FiMail, FiUser, FiShield, FiEdit3, 
  FiTrash2, FiMoreVertical, FiUserPlus, FiUsers
} = FiIcons;

const TeamMembers = () => {
  const { currentWorkspace } = useWorkspace();
  const [members, setMembers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role: 'viewer' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentWorkspace) {
      fetchMembers();
    }
  }, [currentWorkspace]);

  const fetchMembers = async () => {
    try {
      const data = await teamAPI.getMembers(currentWorkspace.id);
      setMembers(data);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    if (!inviteData.email.trim()) return;

    setLoading(true);
    try {
      await teamAPI.inviteMember(currentWorkspace.id, inviteData.email, inviteData.role);
      setInviteData({ email: '', role: 'viewer' });
      setShowInviteModal(false);
      fetchMembers();
    } catch (error) {
      console.error('Failed to invite member:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await teamAPI.updateMember(currentWorkspace.id, memberId, newRole);
      fetchMembers();
    } catch (error) {
      console.error('Failed to update member role:', error);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;

    try {
      await teamAPI.removeMember(currentWorkspace.id, memberId);
      fetchMembers();
    } catch (error) {
      console.error('Failed to remove member:', error);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'editor':
        return 'bg-green-100 text-green-800';
      case 'viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
      case 'admin':
        return FiShield;
      case 'editor':
        return FiEdit3;
      default:
        return FiUser;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your workspace team and permissions
          </p>
        </div>
        
        <button
          onClick={() => setShowInviteModal(true)}
          className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          <SafeIcon icon={FiUserPlus} className="w-4 h-4 mr-2" />
          Invite Member
        </button>
      </div>

      {/* Members List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Team Members ({members.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-200">
          {members.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="px-6 py-4 hover:bg-gray-50"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {member.name?.charAt(0) || member.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="ml-4">
                    <div className="flex items-center">
                      <h4 className="text-sm font-medium text-gray-900">
                        {member.name || 'Unnamed User'}
                      </h4>
                      {member.role === 'owner' && (
                        <SafeIcon icon={FiShield} className="w-4 h-4 text-purple-500 ml-2" />
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{member.email}</p>
                    <div className="flex items-center mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(member.role)}`}>
                        <SafeIcon icon={getRoleIcon(member.role)} className="w-3 h-3 mr-1" />
                        {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {member.role !== 'owner' && (
                    <>
                      <select
                        value={member.role}
                        onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                      </select>
                      
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {members.length === 0 && (
          <div className="px-6 py-12 text-center">
            <SafeIcon icon={FiUsers} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No team members</h3>
            <p className="text-gray-500 mb-4">
              Invite team members to collaborate on this workspace
            </p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <SafeIcon icon={FiUserPlus} className="w-4 h-4 mr-2" />
              Invite First Member
            </button>
          </div>
        )}
      </motion.div>

      {/* Role Permissions Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Role Permissions</h3>
        </div>
        
        <div className="px-6 py-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center mb-2">
                <SafeIcon icon={FiShield} className="w-5 h-5 text-purple-500 mr-2" />
                <h4 className="font-medium text-gray-900">Owner & Admin</h4>
              </div>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Manage QR codes and analytics</li>
                <li>• Invite and manage team members</li>
                <li>• Configure workspace settings</li>
                <li>• Manage custom domains</li>
              </ul>
            </div>
            
            <div>
              <div className="flex items-center mb-2">
                <SafeIcon icon={FiEdit3} className="w-5 h-5 text-green-500 mr-2" />
                <h4 className="font-medium text-gray-900">Editor</h4>
              </div>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Create and edit QR codes</li>
                <li>• View analytics</li>
                <li>• Upload files</li>
                <li>• Create landing pages</li>
              </ul>
            </div>
            
            <div>
              <div className="flex items-center mb-2">
                <SafeIcon icon={FiUser} className="w-5 h-5 text-gray-500 mr-2" />
                <h4 className="font-medium text-gray-900">Viewer</h4>
              </div>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• View QR codes</li>
                <li>• View analytics (read-only)</li>
                <li>• Download QR codes</li>
                <li>• Export reports</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-md mx-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Invite Team Member
            </h3>
            
            <form onSubmit={handleInviteMember}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="colleague@company.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  value={inviteData.role}
                  onChange={(e) => setInviteData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="viewer">Viewer - Can view QR codes and analytics</option>
                  <option value="editor">Editor - Can create and edit QR codes</option>
                  <option value="admin">Admin - Can manage team and settings</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteData({ email: '', role: 'viewer' });
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !inviteData.email.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TeamMembers;